/**
 * ProjectLoader - Loads project based on URL params
 *
 * Handles the /:sessionId/:projectName/* route.
 * Loads project on mount and when projectName changes.
 * Syncs scope path with URL wildcard.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppState } from '../contexts/AppContext';
import { projectService } from '../../serverClient/projectService';
import { webRuntimeApi } from '@/runtime/web';
import { logger } from '@/logging';

interface ProjectLoaderProps {
  children: React.ReactNode;
}

export function ProjectLoader({ children }: ProjectLoaderProps) {
  const { projectName: urlProjectName, draftId } = useParams();
  const { projectName, setProject, logError } = useAppState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track current draft to detect when a new draft is requested
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);

  // Handle new draft projects
  useEffect(() => {
    if (!draftId) return;

    // New draft requested - create empty project
    if (draftId !== currentDraftId) {
      projectService.new();
      setProject(null, null);
      setCurrentDraftId(draftId);
    }
  }, [draftId, currentDraftId, setProject]);

  // Handle saved projects
  useEffect(() => {
    if (!urlProjectName || draftId) return;

    // Already loaded this project
    if (projectName === urlProjectName) return;

    const loadProject = async () => {
      setLoading(true);
      setError(null);

      try {
        // Search for project by name (could be in any subdirectory)
        const tree = await projectService.listTree();
        const fullPath = findProjectPath(tree, urlProjectName);

        if (!fullPath) {
          throw new Error(`Project "${urlProjectName}" not found`);
        }

        logger.info('ProjectLoader', `Loading project: ${fullPath}`);
        await projectService.load(fullPath);
        // Load tweaks sidecar file, or clear stale jukebox state
        try {
          const tweaks = await projectService.loadTweaks(fullPath);
          if (tweaks?.snapshots?.length > 0 || tweaks?.jukeboxEnabled) {
            webRuntimeApi.loadTweaksFile(tweaks);
          } else {
            webRuntimeApi.clearTweaks();
          }
        } catch {
          webRuntimeApi.clearTweaks();
        }
        setProject(urlProjectName, fullPath);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        logError('ProjectLoader', `Failed to load project: ${msg}`);
        setError(msg);
        // Keep URL as-is so HMR reload retries the project
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [urlProjectName, draftId, projectName, setProject, logError]);

  // Always render children so RuntimeProvider mounts and subscribes
  // before project loading completes. Show loading/error as overlay.
  return (
    <>
      {children}
      {loading && (
        <div style={styles.overlay}>
          <div style={styles.message}>Loading project...</div>
        </div>
      )}
      {error && (
        <div style={styles.overlay}>
          <div style={styles.error}>{error}</div>
        </div>
      )}
    </>
  );
}

/**
 * Find project path by name in the file tree
 * Matches with or without .json extension
 */
function findProjectPath(node: any, name: string): string | null {
  if (!node.isDirectory) {
    // Match name with or without .json extension
    const nodeName = node.name?.replace(/\.json$/, '');
    if (nodeName === name) {
      return node.path;
    }
  }

  if (node.children) {
    for (const child of node.children) {
      const found = findProjectPath(child, name);
      if (found) return found;
    }
  }

  return null;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(17, 24, 39, 0.9)',
    color: 'white',
    zIndex: 1000
  },
  message: {
    color: '#9ca3af',
    fontSize: 16
  },
  error: {
    color: '#ef4444',
    fontSize: 16
  }
};
